import React from 'react';
import factoriesSvg from '../../assets/svg/factories.svg';
import farmersSvg from '../../assets/svg/farmers.svg';
import forestSvg from '../../assets/svg/forests.svg';
import { useLabels } from '../../hooks/useLabels';
import { getFarmerCardsQuery, getImpactQuery } from '../Dashboard/queries';
import {
  Box,
  Button,
  ButtonWrapper,
  Container,
  InfoBlock,
  InfoContainer,
  LargeText,
  PeriodHint,
  SmallText,
  WrapperInfoBlock,
} from './Header.styles';

interface HeaderProps {
  type: 'factories' | 'farmers' | 'forests' | '';
  // Narrowed from Dispatch<SetStateAction<…>>: the parent now passes a wrapper
  // (handleTypeChange) that also clears the drill-down, and Header only ever
  // calls it with plain values.
  setType: (value: '' | 'factories' | 'farmers' | 'forests') => void;
  onZoomOut: () => void;
}

// Thousands separator for a plain digit string ("189000" → "189,000"). Anything
// else - "na", blank, already-formatted "115,795kg", a decimal like "5.858" -
// is returned untouched, so a non-number is never disguised as one (§10).
export const formatThousands = (value: string): string =>
  /^\d+$/.test(value) ? Number(value).toLocaleString('en-GB') : value;

export const Header: React.FC<HeaderProps> = ({ type, setType, onZoomOut }) => {
  const { data, isLoading: isImpactLoading } = getImpactQuery();
  // Every visible word below comes from key_impact_data_live.labels_json via t(); the
  // second argument is the exact text production showed before PR #7 and is
  // what renders while the layer is absent, empty or failing (founder decision
  // 2026-09-06). Caption keys fall back to '' and render nothing.
  const { t } = useLabels();

  // Null-guard the feature access (audit flagged features[0] as a crash risk if
  // the layer returns an empty FeatureCollection).
  const impactData = data?.features?.[0]?.properties;

  // Headline farmer count now comes from farmer_cards_public, summed across the
  // 5 cohorts (= 13,391), replacing key_impact_data_live.farmers. Loading, empty
  // and error stay distinct visible states (§10/§13): a missing/failed layer
  // shows "Unavailable", never a blank or a zero.
  const { data: farmerCards, isLoading: isFarmerCardsLoading } = getFarmerCardsQuery();
  const farmerTotal = farmerCards?.features?.reduce(
    (sum, feature) => sum + (feature.properties.farmer_count ?? 0),
    0,
  );
  const farmerTotalLabel =
    farmerTotal != null && farmerTotal > 0 ? farmerTotal.toLocaleString() : null;

  // A headline value: "…" while the impact layer loads, "Unavailable" when the
  // field is missing (previously this rendered the literal text "undefined"),
  // otherwise the served value with its optional unit word.
  const impactValue = (field: string, unit?: string): string => {
    if (isImpactLoading) return '…';
    const raw = impactData?.[field];
    if (raw == null || String(raw).trim() === '') return 'Unavailable';
    const value = String(raw);
    return unit ? `${value} ${unit}` : value;
  };

  // One headline tile: value, label row, optional caption row (period hint such
  // as "since 2012" - D9's "as at", served as data, shown only when the DB has it).
  const tile = (field: string, value: string, fallbackLabel: string, isMain = false) => {
    const caption = t(`headline.${field}.caption`, '');
    return (
      <WrapperInfoBlock isMain={isMain}>
        <InfoBlock isMain={isMain}>
          <LargeText>{value}</LargeText>
          <SmallText>{t(`headline.${field}.label`, fallbackLabel)}</SmallText>
          {caption !== '' && <PeriodHint>{caption}</PeriodHint>}
        </InfoBlock>
      </WrapperInfoBlock>
    );
  };

  return (
    <Container>
      <Box>
        <ButtonWrapper>
          <Button
            isActive={type === 'factories'}
            type={type}
            onClick={() => {
              type === 'factories' ? setType('') : setType('factories');
              onZoomOut();
            }}
          >
            <img src={factoriesSvg} />
            <p>Factories</p>
          </Button>
          {type === 'factories' && (
            <InfoContainer type={type}>
              {tile(
                'money_left_at_origin',
                impactValue('money_left_at_origin'),
                'Money left at origin',
                true,
              )}
              {tile(
                'jobs_supported_in_origin',
                impactValue('jobs_supported_in_origin'),
                'Jobs supported',
              )}
              {tile(
                'kg_roasted_last_year',
                isImpactLoading || impactData?.kg_roasted_last_year == null
                  ? impactValue('kg_roasted_last_year')
                  : `${formatThousands(String(impactData.kg_roasted_last_year))} ${t(
                      'headline.kg_roasted_last_year.unit',
                      'kg',
                    )}`,
                'Roasted last year',
              )}
              {tile(
                'm_cups_drank',
                impactValue('m_cups_drank', t('headline.m_cups_drank.unit', 'million')),
                'Cups drank',
              )}
            </InfoContainer>
          )}
        </ButtonWrapper>

        <ButtonWrapper>
          <Button
            isActive={type === 'farmers'}
            type={type}
            onClick={() => {
              type === 'farmers' ? setType('') : setType('farmers');
              onZoomOut();
            }}
          >
            <img src={farmersSvg} />
            <p>Farmers</p>
          </Button>
          {type === 'farmers' && (
            <InfoContainer type={type}>
              {tile(
                'farmers',
                isFarmerCardsLoading ? '…' : farmerTotalLabel ?? 'Unavailable',
                'Farmers',
                true,
              )}
              {tile('impact_investments', impactValue('impact_investments'), 'Impact investment')}
              {tile('premium_paid', impactValue('premium_paid'), 'Premium Paid')}
            </InfoContainer>
          )}
        </ButtonWrapper>

        <ButtonWrapper>
          <Button
            isActive={type === 'forests'}
            type={type}
            onClick={() => {
              type === 'forests' ? setType('') : setType('forests');
              onZoomOut();
            }}
          >
            <img src={forestSvg} />
            <p>Forests</p>
          </Button>
          {type === 'forests' && (
            <InfoContainer type={type}>
              {tile(
                'ton_co2_sequestered',
                impactValue('ton_co2_sequestered'),
                'MTON CO2 SURPLUS',
                true,
              )}
              {tile(
                'ha_forest_protected',
                impactValue('ha_forest_protected', t('headline.ha_forest_protected.unit', 'HA')),
                'Forest Protected',
              )}
              {/* Fallback keeps production's "Tress Planted" spelling on purpose:
                  the rule is "exact pre-PR text", and the corrected label is a
                  labels_json entry (headline.coffee_trees_planted.label). */}
              {tile('coffee_trees_planted', impactValue('coffee_trees_planted'), 'Tress Planted')}
              {tile('deforestation_free', impactValue('deforestation_free'), 'Deforestation Free')}
            </InfoContainer>
          )}
        </ButtonWrapper>
      </Box>
    </Container>
  );
};
